import React, { useEffect, useState } from 'react';
import { BarChart3, Package, Scale, TrendingUp, Globe } from 'lucide-react';
import api from '../api';

const MESES_LABEL = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const ORIGEN_LABEL = { usa: 'Miami', china: 'China' };
const ORIGEN_COLOR = { usa: '#3b82f6', china: '#ef4444' };

function KpiCard({ icon: Icon, label, valor, sub, color }) {
  return (
    <div style={{
      background: 'var(--color-bg)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-md)', padding: '16px 20px',
      display: 'flex', alignItems: 'center', gap: '14px',
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: '10px',
        background: (color || 'var(--color-primary)') + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={20} style={{ color: color || 'var(--color-primary)' }} />
      </div>
      <div>
        <div style={{ fontSize: '22px', fontWeight: 700, lineHeight: 1.1 }}>{valor}</div>
        <div className="text-sm text-secondary">{label}</div>
        {sub && <div className="text-xs text-secondary">{sub}</div>}
      </div>
    </div>
  );
}

function BarraProgreso({ usado, limite }) {
  const pct = limite > 0 ? Math.min((usado / limite) * 100, 100) : 0;
  const color = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#22c55e';
  return (
    <div style={{
      background: 'var(--color-bg)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-md)', padding: '16px 20px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
        <div>
          <span style={{ fontSize: '22px', fontWeight: 700 }}>${usado.toFixed(2)}</span>
          <span className="text-secondary text-sm"> / ${limite.toFixed(2)}</span>
        </div>
        <span style={{ fontSize: '13px', fontWeight: 600, color }}>{pct.toFixed(0)}%</span>
      </div>
      <div style={{ height: '8px', background: 'var(--color-bg-secondary)', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '4px', transition: 'width 0.5s ease' }} />
      </div>
      <div className="text-xs text-secondary" style={{ marginTop: '6px' }}>
        Límite anual Categoría B — te quedan ${(limite - usado).toFixed(2)}
      </div>
    </div>
  );
}

function MiniBarChart({ datos, labelKey, valueKey, color, prefix }) {
  const max = Math.max(...datos.map(d => d[valueKey]), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '100px' }}>
      {datos.map((d, i) => {
        const h = max > 0 ? (d[valueKey] / max) * 80 : 0;
        const mesActual = new Date().getMonth();
        const opacity = i <= mesActual ? 1 : 0.3;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', opacity }}>
            <div className="text-xs" style={{ fontSize: '9px', color: 'var(--color-text-secondary)' }}>
              {d[valueKey] > 0 ? `${prefix || ''}${d[valueKey]}` : ''}
            </div>
            <div style={{
              width: '100%', maxWidth: '28px', height: `${Math.max(h, 2)}px`,
              background: d[valueKey] > 0 ? (color || 'var(--color-primary)') : 'var(--color-bg-secondary)',
              borderRadius: '3px 3px 0 0',
            }} />
            <div className="text-xs" style={{ fontSize: '9px', color: 'var(--color-text-secondary)' }}>
              {MESES_LABEL[d[labelKey] - 1]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OrigenDesglose({ por_origen }) {
  const total = Object.values(por_origen).reduce((s, v) => s + v, 0) || 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {Object.entries(por_origen).map(([origen, cant]) => (
        <div key={origen} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '10px', height: '10px', borderRadius: '50%',
            background: ORIGEN_COLOR[origen] || '#9ca3af',
          }} />
          <span className="text-sm" style={{ flex: 1 }}>{ORIGEN_LABEL[origen] || origen}</span>
          <strong className="text-sm">{cant}</strong>
          <span className="text-xs text-secondary">{((cant / total) * 100).toFixed(0)}%</span>
        </div>
      ))}
      {Object.keys(por_origen).length === 0 && (
        <span className="text-sm text-secondary">Sin datos</span>
      )}
    </div>
  );
}

export default function DashboardCliente() {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    api.get('/cliente/dashboard/')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setCargando(false));
  }, []);

  if (cargando) return <p className="text-secondary">Cargando dashboard...</p>;
  if (!data) return null;

  return (
    <div style={{ marginBottom: 'var(--spacing-6)' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)',
      }}>
        <BarraProgreso usado={data.valor_importado} limite={data.limite_anual} />
        <KpiCard icon={Package} label="Importaciones del año" valor={data.importaciones_anio} color="#8b5cf6" />
        <KpiCard icon={TrendingUp} label="Paquetes activos" valor={data.paquetes_activos} color="#0ea5e9" />
        <KpiCard icon={Scale} label="Peso importado (lb)" valor={`${data.peso_total_lb.toFixed(1)}`} color="#f59e0b" />
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 'var(--spacing-3)',
      }}>
        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)', padding: '16px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <BarChart3 size={16} style={{ color: 'var(--color-text-secondary)' }} />
            <strong className="text-sm">Importaciones por mes</strong>
          </div>
          <MiniBarChart datos={data.importaciones_por_mes} labelKey="mes" valueKey="cantidad" color="#8b5cf6" />
        </div>

        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)', padding: '16px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <BarChart3 size={16} style={{ color: 'var(--color-text-secondary)' }} />
            <strong className="text-sm">Gasto courier por mes</strong>
          </div>
          <MiniBarChart datos={data.gasto_courier_por_mes} labelKey="mes" valueKey="monto" color="#0ea5e9" prefix="$" />
        </div>

        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)', padding: '16px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Globe size={16} style={{ color: 'var(--color-text-secondary)' }} />
            <strong className="text-sm">Origen de paquetes ({data.anio})</strong>
          </div>
          <OrigenDesglose por_origen={data.por_origen} />
        </div>
      </div>
    </div>
  );
}
